// FocusPlanFlow.tsx — the AI-deepening feature (PROGRAM-DESIGN-style, mirrors Workout Tracker's
// ProgramFlow.tsx). Renders the Timer-screen state machine: no active plan -> "Draft a focus plan"
// CTA -> drafting -> review/edit the AI's proposal -> Accept (writes real focus_plan +
// planned_session rows) or Discard (nothing saved) -> once accepted, a NEXT hero shows the
// soonest uncompleted slot with a Start button, pre-filling the Timer.
import { useCallback, useEffect, useRef, useState } from "react";
import { store, type FocusPlan, type PlannedSession, type Band, type WeeklySignals } from "./store";
import { draftFocusPlan, BrainError, type DraftFocusPlanResult, type DraftPlanSession } from "./brain";

const WEEKDAY_LABEL = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const BAND_OPTIONS: Band[] = ["morning", "afternoon", "evening", "night"];
const BAND_DOT: Record<Band, string> = { morning: "#f5a524", afternoon: "#5b4fcf", evening: "#7c6fe0", night: "#4b4f66" };

function BandDot({ band }: { band: Band }) {
  return <span aria-hidden style={{ width: 8, height: 8, borderRadius: "50%", background: BAND_DOT[band], flexShrink: 0, display: "inline-block" }} />;
}

function ModalShell({ title, onCancel, children }: { title: string; onCancel: () => void; children: React.ReactNode }) {
  return (
    <div className="modal-overlay">
      <div className="modal-card" style={{ width: "100%", maxWidth: 560, maxHeight: "85vh", overflow: "auto" }}>
        <div className="row" style={{ justifyContent: "space-between", marginBottom: "1rem" }}>
          <div style={{ fontWeight: 700, fontSize: "var(--fs-heading)" }}>{title}</div>
          <button className="btn btn-ghost" style={{ padding: "0.3rem" }} onClick={onCancel} aria-label="Close">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function SessionRow({ s, onChange, onRemove }: { s: DraftPlanSession; onChange: (s: DraftPlanSession) => void; onRemove: () => void }) {
  return (
    <div style={{ borderTop: "1px solid var(--border)", padding: "0.6rem 0" }}>
      <div className="row" style={{ gap: "0.4rem", flexWrap: "wrap", alignItems: "center" }}>
        <BandDot band={s.band} />
        <select value={s.weekday} onChange={(e) => onChange({ ...s, weekday: Number(e.target.value) })} style={{ width: "auto", fontSize: "var(--fs-sm)" }}>
          {WEEKDAY_LABEL.map((d, i) => <option key={i} value={i}>{d}</option>)}
        </select>
        <select value={s.band} onChange={(e) => onChange({ ...s, band: e.target.value as Band })} style={{ width: "auto", fontSize: "var(--fs-sm)" }}>
          {BAND_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <input type="number" min="5" step="5" value={s.duration_min} onChange={(e) => onChange({ ...s, duration_min: Number(e.target.value) || 25 })}
          title="Minutes" style={{ width: 64, fontSize: "var(--fs-sm)" }} />
        <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>min</span>
        <button className="btn btn-ghost" style={{ marginLeft: "auto", padding: "0.25rem", color: "var(--ink-3)" }} onClick={onRemove} aria-label="Remove">✕</button>
      </div>
      <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 4, fontStyle: "italic" }}>{s.reason}</div>
    </div>
  );
}

function PlanDraftReview({ draft, onAccept, onDiscard }: { draft: DraftFocusPlanResult; onAccept: (sessions: DraftPlanSession[], name: string) => void; onDiscard: () => void }) {
  const [sessions, setSessions] = useState<DraftPlanSession[]>(draft.sessions);
  return (
    <ModalShell title={draft.name} onCancel={onDiscard}>
      {sessions.map((s, i) => (
        <SessionRow key={i} s={s}
          onChange={(v) => setSessions((cur) => cur.map((x, xi) => xi === i ? v : x))}
          onRemove={() => setSessions((cur) => cur.filter((_, xi) => xi !== i))} />
      ))}
      <div className="row" style={{ gap: "0.5rem", justifyContent: "flex-end", marginTop: "1rem" }}>
        <button className="btn" onClick={onDiscard}>Discard</button>
        <button className="btn btn-primary" disabled={sessions.length === 0} onClick={() => onAccept(sessions, draft.name)}>Accept — start this plan</button>
      </div>
    </ModalShell>
  );
}

export function FocusPlanSection({ weeklySignals, onPlanChanged, onStart, refreshSignal, triggerDraft }: {
  weeklySignals: () => Promise<WeeklySignals>;
  onPlanChanged: () => void;
  onStart: (session: PlannedSession) => void;
  refreshSignal: number;
  triggerDraft?: number;
}) {
  const [active, setActive] = useState<FocusPlan | null>(null);
  const [next, setNext] = useState<PlannedSession | null>(null);
  const [draft, setDraft] = useState<DraftFocusPlanResult | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const [a, n] = await Promise.all([store.activePlan(), store.nextPlannedSession()]);
    setActive(a); setNext(n);
  }, []);
  useEffect(() => { void reload(); }, [reload, refreshSignal]);

  async function runDraft() {
    setDrafting(true); setErr(null);
    try {
      const signals = await weeklySignals();
      const result = await draftFocusPlan(signals);
      setDraft(result);
    } catch (e) {
      setErr(e instanceof BrainError ? e.message : "Couldn't draft a plan — please try again.");
    } finally {
      setDrafting(false);
    }
  }

  const draftTriggerSeen = useRef(triggerDraft ?? 0);
  useEffect(() => {
    if (triggerDraft !== undefined && triggerDraft !== draftTriggerSeen.current) {
      draftTriggerSeen.current = triggerDraft;
      void runDraft();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerDraft]);

  async function accept(sessions: DraftPlanSession[], name: string) {
    await store.createFocusPlan({ name, sessions });
    setDraft(null);
    await reload();
    onPlanChanged();
  }

  if (draft) return <PlanDraftReview draft={draft} onDiscard={() => setDraft(null)} onAccept={(s, n) => void accept(s, n)} />;

  if (active && next) {
    return (
      <div className="card card-in" style={{ padding: "1.1rem 1.25rem 1.25rem", marginBottom: "1.5rem", textAlign: "left" }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.7rem" }}>
          <div>
            <div className="micro" style={{ color: "var(--accent)" }}>Next planned session</div>
            <div className="row" style={{ gap: "0.45rem", marginTop: 4 }}>
              <BandDot band={next.band} />
              <span style={{ fontWeight: 700, fontSize: "var(--fs-heading)" }}>{WEEKDAY_LABEL[next.weekday]} · {next.label}</span>
            </div>
          </div>
          <button className="btn btn-primary" onClick={() => onStart(next)}>Start</button>
        </div>
        <div className="muted" style={{ fontSize: "var(--fs-sm)" }}>{next.duration_min} min · {next.band} · {next.reason}</div>
      </div>
    );
  }

  return (
    <div className="card card-in" style={{ padding: "1rem 1.25rem", marginBottom: "1.5rem", textAlign: "left" }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontWeight: 600, fontSize: "var(--fs-md)" }}>✦ Draft a focus plan</div>
        <button className="btn btn-primary" disabled={drafting} onClick={() => void runDraft()}>{drafting ? "Drafting…" : "Start"}</button>
      </div>
      <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0.4rem 0 0", lineHeight: 1.5 }}>
        The AI designs a real week of focus sessions from your own patterns — you review and edit it before anything's saved.
      </p>
      {err && <div style={{ marginTop: 8, color: "var(--danger)", fontSize: "var(--fs-xs)" }}>{err}</div>}
    </div>
  );
}
